import { createContext, useContext, useEffect, useState } from "react";
import { X } from "lucide-react";
import StrategyFunnel, { GETLEADS_COPY } from "./StrategyFunnel";

// Site-wide "book a call" modal: every CTA on the main site pops the quiz funnel
// as an overlay instead of navigating away. Safe default navigates to /contact
// if a component ever renders outside the provider.
const BookCtx = createContext({ openBook: () => { window.location.href = "/contact"; } });
export const useSiteBook = () => useContext(BookCtx);

export function SiteBookProvider({ children }) {
  const [open, setOpen] = useState(false);

  // This app's scrolling root is <html>, not <body> — lock both. Desktop also
  // runs Lenis smooth-scroll (App.js), which drives scroll programmatically
  // via rAF, so overflow:hidden alone won't stop it — pause Lenis too.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    if (open) {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      window.__lenis?.stop();
    }
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      window.__lenis?.start();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <BookCtx.Provider value={{ openBook: () => setOpen(true) }}>
      {children}
      {open && (
        <div
          data-testid="book-modal"
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-navy/60 p-4 backdrop-blur-sm sm:items-center"
          onClick={() => setOpen(false)}
        >
          <div className="relative my-6 w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="book-modal-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="absolute -top-4 right-0 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-navy shadow-lg transition-transform hover:scale-105 sm:-right-4"
            >
              <X className="h-4 w-4" />
            </button>
            <StrategyFunnel id="modal-funnel" copy={GETLEADS_COPY} src="site" showPreview={false} />
          </div>
        </div>
      )}
    </BookCtx.Provider>
  );
}
